import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import Particles from "react-tsparticles";
import type { Container, Engine } from "tsparticles-engine";
import { loadSlim } from "tsparticles-slim"; // Using slim version for basic particles
import { BsStars } from "react-icons/bs";
import confetti from 'canvas-confetti';


const prompts = [
  "What is Neuroline Ki?",
  "Find AI tools for productivity",
  "Explain deep learning in simple terms",
  "Generate insights from data",
  "How can AI improve my workflow?",
];

const brandName = "neUro Ki";

interface ConfettiOptions {
  spread: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
}

const HeroSection = () => {
  const [glitchActive, setGlitchActive] = useState(false);
  const [inputText, setInputText] = useState("");
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [displayedText, setDisplayedText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);
  const [ballPosition, setBallPosition] = useState(0);
  const [ballLanded, setBallLanded] = useState(false);

  const fireConfetti = useCallback(() => {
    // Fire multiple bursts of confetti
    const duration = 15 * 100;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: any = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Since particles fall down, start a bit higher than random
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FF69B4', '#00BFFF', '#98FB98', '#DDA0DD'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#FFD700', '#FF69B4', '#00BFFF', '#98FB98', '#DDA0DD'],
      });
    }, 250);
  }, []);

  const letterSpacing = 33;
  const typingSpeed = 150;

  useEffect(() => {
    if (ballLanded && typingIndex < brandName.length) {
      const typingTimeout = setTimeout(() => {
        setDisplayedText((prev) => prev + brandName[typingIndex]);
        setBallPosition((typingIndex + 1) * letterSpacing);
        setTypingIndex((prev) => prev + 1);
      }, typingSpeed);

      return () => clearTimeout(typingTimeout);
    } else if (ballLanded) {
      setTimeout(() => {
        setDisplayedText("");
        setTypingIndex(0);
        setBallPosition(0);
      }, 2000);
    }
  }, [typingIndex, ballLanded]);

  useEffect(() => {
    if (!isUserTyping) {
      const interval = setInterval(() => {
        setCurrentPrompt((prev) => (prev + 1) % prompts.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isUserTyping]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // New initialization function using useCallback
  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
  }, []);

  // Optional callback after particles load
  const particlesLoaded = useCallback(async (container: Container | undefined) => {
    console.log("Particles loaded:", container);
  }, []);

  return (
    <div className="relative flex items-center justify-center min-h-screen bg-black overflow-hidden">
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 object-cover w-full h-full"
      >
        <source src="/assets/" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Particles Overlay */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={{
          fullScreen: false,
          background: {
            color: {
              value: "transparent",
            },
          },
          fpsLimit: 120,
          interactivity: {
            events: {
              onClick: {
                enable: true,
                mode: "push",
              },
              onHover: {
                enable: true,
                mode: "repulse",
              },
              resize: true,
            },
            modes: {
              push: {
                quantity: 4,
              },
              repulse: {
                distance: 200,
                duration: 0.4,
              },
            },
          },
          particles: {
            color: {
              value: "#ffffff",
            },
            links: {
              color: "#ffffff",
              distance: 150,
              enable: true,
              opacity: 0.5,
              width: 1,
            },
            collisions: {
              enable: true,
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "bounce",
              },
              random: false,
              speed: 1,
              straight: false,
            },
            number: {
              density: {
                enable: true,
                area: 800,
              },
              value: 80,
            },
            opacity: {
              value: 0.5,
            },
            shape: {
              type: "circle",
            },
            size: {
              value: { min: 1, max: 5 },
            },
          },
          detectRetina: true,
        }}
        className="absolute inset-0 pointer-events-none"
      />

      {/* Rest of the component remains the same */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black opacity-60"></div>

      <div className="relative z-10 flex flex-col items-center px-4 text-center">
        <div className="relative">
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/10 to-gray-800/20 blur-3xl opacity-50"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div className="relative flex items-center">
            <motion.h1
              className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg min-w-[250px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              {displayedText}
            </motion.h1>

            <motion.div
              className="absolute w-6 h-6 md:w-16 md:h-16 bg-white rounded-full shadow-lg"
              initial={{ opacity: 0, y: -100 }} // Start above the screen
              animate={{
                opacity: 1,
                y: 0, // Drop down to normal position
                x: ballPosition, // Moves dynamically per letter
              }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 15,
                duration: 1,
              }}
              onAnimationComplete={() => setBallLanded(true)} // Start typing once the ball lands
            />
          </motion.div>
        </div>

        <motion.p
          className="mt-6 text-lg sm:text-2xl text-gray-300 max-w-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1 }}
        >
          Where AI meets next generation creativity and intelligence.
        </motion.p>

        <motion.div
          className="relative mt-10 w-full max-w-3xl h-34 bg-white/10 backdrop-blur-lg text-white rounded-3xl px-6 py-6 flex items-center justify-between shadow-xl text-2xl"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 1 }}
        >
          {/* Input Field */}
          <input
            type="text"
            className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-xl"
            placeholder={prompts[currentPrompt]}
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
              setIsUserTyping(e.target.value.length > 0);
            }}
            onBlur={() => setIsUserTyping(false)}
          />

          <motion.button
            className="relative bg-gray-700/50 p-5 rounded-full hover:bg-gray-600/50 transition-all group glow-effect hover-glow"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {/* Glow effect overlay */}
            <div className="absolute inset-0 rounded-full bg-white opacity-0 group-hover:opacity-20 transition-opacity" />

            {/* Star icon with animation */}
            <div className="spin-slow">
              <BsStars className="text-white text-3xl" />
            </div>

            {/* Pulse effect */}
            <div className="absolute inset-0 rounded-full animate-ping bg-white/10" />
          </motion.button>
        </motion.div>
        <motion.div
          className="mt-6 flex flex-wrap justify-center gap-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          {["Search with AI", "Ask Neuroline Ki", "Explore AI Utilities", "More"].map((label, index) => (
            <motion.button
              key={index}
              className="px-6 py-3 border border-gray-500/50 text-gray-300 rounded-full text-lg hover:bg-gray-700/50 transition-all backdrop-blur-md"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fireConfetti}
            >
              {label}
            </motion.button>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default HeroSection;





































// import React, { useState, useEffect } from "react";
// import { motion } from "framer-motion";
// import { BsStars } from "react-icons/bs";

// const prompts = [
//   "What is Neuroline Ki?",
//   "Find AI tools for productivity",
//   "Explain deep learning in simple terms",
//   "Generate insights from data",
//   "How can AI improve my workflow?",
// ];

// const brandName = "neUro Ki";

// const HeroSection = () => {
// const [inputText, setInputText] = useState("");
// const [currentPrompt, setCurrentPrompt] = useState(0);
// const [isUserTyping, setIsUserTyping] = useState(false);
// const [displayedText, setDisplayedText] = useState("");
// const [typingIndex, setTypingIndex] = useState(0);
// const [ballPosition, setBallPosition] = useState(0);
// const [ballLanded, setBallLanded] = useState(false);

// const letterSpacing = 33;
// const typingSpeed = 150;

// useEffect(() => {
//   if (ballLanded && typingIndex < brandName.length) {
//     const typingTimeout = setTimeout(() => {
//       setDisplayedText((prev) => prev + brandName[typingIndex]);
//       setBallPosition((typingIndex + 1) * letterSpacing);
//       setTypingIndex((prev) => prev + 1);
//     }, typingSpeed);

//     return () => clearTimeout(typingTimeout);
//   } else if (ballLanded) {
//     setTimeout(() => {
//       setDisplayedText("");
//       setTypingIndex(0);
//       setBallPosition(0);
//     }, 2000);
//   }
// }, [typingIndex, ballLanded]);

// useEffect(() => {
//   if (!isUserTyping) {
//     const interval = setInterval(() => {
//       setCurrentPrompt((prev) => (prev + 1) % prompts.length);
//     }, 3000);

//     return () => clearInterval(interval);
//   }
// }, [isUserTyping]);

//   return (
//     <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white px-4 relative overflow-hidden">

// <motion.div
//   className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/10 to-gray-800/20 blur-3xl opacity-50"
//   animate={{ scale: [1, 1.2, 1] }}
//   transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
// />
// <motion.div className="relative flex items-center">
//   <motion.h1
//     className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg min-w-[250px]"
//     initial={{ opacity: 0 }}
//     animate={{ opacity: 1 }}
//     transition={{ duration: 1 }}
//   >
//     {displayedText}
//   </motion.h1>

//   <motion.div
//     className="absolute w-6 h-6 md:w-16 md:h-16 bg-white rounded-full shadow-lg"
//     initial={{ opacity: 0, y: -100 }} // Start above the screen
//     animate={{
//       opacity: 1,
//       y: 0, // Drop down to normal position
//       x: ballPosition, // Moves dynamically per letter
//     }}
//     transition={{
//       type: "spring",
//       stiffness: 100,
//       damping: 15,
//       duration: 1,
//     }}
//     onAnimationComplete={() => setBallLanded(true)} // Start typing once the ball lands
//   />
// </motion.div>

//       {/* Chat Box (User Input + Fading Prompts) */}
// <motion.div
//   className="relative mt-10 w-full max-w-3xl h-34 bg-white/10 backdrop-blur-xl text-white rounded-3xl px-6 py-6 flex items-center justify-between shadow-xl text-2xl"
//   initial={{ opacity: 0, y: 10 }}
//   animate={{ opacity: 1, y: 0 }}
//   transition={{ delay: 1, duration: 1 }}
// >
//   {/* Input Field */}
//   <input
//     type="text"
//     className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-xl"
//     placeholder={prompts[currentPrompt]}
//     value={inputText}
//     onChange={(e) => {
//       setInputText(e.target.value);
//       setIsUserTyping(e.target.value.length > 0);
//     }}
//     onBlur={() => setIsUserTyping(false)}
//   />

//   <motion.button
//     className="bg-gray-700/50 p-5 rounded-full hover:bg-gray-600/50 transition-all"
//     whileHover={{ scale: 1.1 }}
//     whileTap={{ scale: 0.9 }}
//   >
//     <BsStars className="text-white text-3xl" />
//   </motion.button>
// </motion.div>

// <motion.div
//   className="mt-6 flex flex-wrap justify-center gap-4"
//   initial={{ opacity: 0, y: 10 }}
//   animate={{ opacity: 1, y: 0 }}
//   transition={{ delay: 1.5, duration: 1 }}
// >
//   {["Search with AI", "Ask Neuroline Ki", "Explore AI Utilities", "More"].map((label, index) => (
//     <button
//       key={index}
//       className="px-6 py-3 border border-gray-500/50 text-gray-300 rounded-full text-lg hover:bg-gray-700/50 transition-all backdrop-blur-md"
//     >
//       {label}
//     </button>
//   ))}
// </motion.div>
//     </div>
//   );
// };

// export default HeroSection;


// import React, { useState, useEffect } from "react";
// import { motion } from "framer-motion";

// const prompts = [
//   "What is Neuroline Ki?",
//   "Find AI tools for productivity",
//   "Explain deep learning in simple terms",
//   "Generate insights from data",
//   "How can AI improve my workflow?",
// ];

// const brandName = "neUro Ki";

// const HeroSection = () => {
//   const [inputText, setInputText] = useState("");
//   const [currentPrompt, setCurrentPrompt] = useState(0);
//   const [isUserTyping, setIsUserTyping] = useState(false);
//   const [displayedText, setDisplayedText] = useState("");
//   const [typingIndex, setTypingIndex] = useState(0);
//   const [ballPosition, setBallPosition] = useState(0);
//   const [ballLanded, setBallLanded] = useState(false);

//   const letterSpacing = 30; // Adjust spacing for smooth ball alignment
//   const typingSpeed = 250; // Speed of typing effect

// useEffect(() => {
//   if (ballLanded && typingIndex < brandName.length) {
//     const typingTimeout = setTimeout(() => {
//       setDisplayedText((prev) => prev + brandName[typingIndex]); // Append letters one by one
//       setBallPosition((typingIndex + 1) * letterSpacing); // Move ball accordingly
//       setTypingIndex((prev) => prev + 1);
//     }, typingSpeed);

//     return () => clearTimeout(typingTimeout);
//   } else if (ballLanded) {
//     setTimeout(() => {
//       setDisplayedText(""); // Clear text after pause
//       setTypingIndex(0); // Restart typing
//       setBallPosition(0); // Reset ball to start
//     }, 2000); // Wait before restarting
//   }
// }, [typingIndex, ballLanded]);

//   useEffect(() => {
//     if (!isUserTyping) {
//       const interval = setInterval(() => {
//         setCurrentPrompt((prev) => (prev + 1) % prompts.length);
//       }, 3000); // Change prompt every 3 seconds

//       return () => clearInterval(interval);
//     }
//   }, [isUserTyping]);

//   return (
//     <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white px-4 relative overflow-hidden">
//       {/* Background Animation */}
//       <motion.div
//         className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/10 to-gray-800/20 blur-3xl opacity-50"
//         animate={{ scale: [1, 1.2, 1] }}
//         transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//       />

//       {/* Typing Brand Name with Ball Animation */}
//       <motion.div className="relative flex items-center">
//         {/* Placeholder to prevent misalignment */}
//         <motion.h1
//           className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg min-w-[250px]"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 1 }}
//         >
//           {displayedText}
//         </motion.h1>

//         {/* Ball Animation (Drops from Top Before Typing) */}
//   <motion.div
//     className="absolute w-5 h-5 md:w-6 md:h-6 bg-white rounded-full shadow-lg"
//     initial={{ opacity: 0, y: -100 }} // Start above the screen
//     animate={{
//       opacity: 1,
//       y: 0, // Drop down to normal position
//       x: ballPosition, // Moves dynamically per letter
//     }}
//     transition={{
//       type: "spring",
//       stiffness: 100,
//       damping: 15,
//       duration: 1,
//     }}
//     onAnimationComplete={() => setBallLanded(true)} // Start typing once the ball lands
//   />
// </motion.div>

//       {/* Chat Box (User Input + Fading Prompts) */}
//       <motion.div
//         className="relative mt-10 w-full max-w-3xl h-24 bg-white/10 backdrop-blur-xl text-white rounded-3xl px-6 py-6 flex items-center justify-between shadow-xl text-2xl"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1, duration: 1 }}
//       >
//         {/* Input Field */}
//         <input
//           type="text"
//           className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-xl"
//           placeholder={prompts[currentPrompt]}
//           value={inputText}
//           onChange={(e) => {
//             setInputText(e.target.value);
//             setIsUserTyping(e.target.value.length > 0);
//           }}
//           onBlur={() => setIsUserTyping(false)}
//         />

//         {/* Animated Icon */}
//         <motion.button
//           className="bg-gray-700/50 p-5 rounded-full hover:bg-gray-600/50 transition-all"
//           whileHover={{ scale: 1.1 }}
//           whileTap={{ scale: 0.9 }}
//         >
//           <div className="text-white text-3xl">✨</div>
//         </motion.button>
//       </motion.div>

//       {/* Quick Actions */}
//       <motion.div
//         className="mt-6 flex flex-wrap justify-center gap-4"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1.5, duration: 1 }}
//       >
//         {["Search with AI", "Ask Neuroline Ki", "Explore AI Utilities", "More"].map((label, index) => (
//           <button
//             key={index}
//             className="px-6 py-3 border border-gray-500/50 text-gray-300 rounded-full text-lg hover:bg-gray-700/50 transition-all backdrop-blur-md"
//           >
//             {label}
//           </button>
//         ))}
//       </motion.div>
//     </div>
//   );
// };

// export default HeroSection;


// import React, { useState, useEffect } from "react";
// import { motion } from "framer-motion";
// import { BsStars } from "react-icons/bs"; // Replacing ball with BsStars icon

// const prompts = [
//   "Talk with AI Agents?",
//   "Find AI tools for productivity",
//   "How can AI improve my workflow?",
// ];

// const brandName = "neUro Ki";

// const HeroSection = () => {
//   const [inputText, setInputText] = useState("");
//   const [currentPrompt, setCurrentPrompt] = useState(0);
//   const [isUserTyping, setIsUserTyping] = useState(false);
//   const [displayedText, setDisplayedText] = useState("");
//   const [typingIndex, setTypingIndex] = useState(0);
//   const [starPosition, setStarPosition] = useState(0);
//   const [starLanded, setStarLanded] = useState(false);

//   const letterSpacing = 33; // Adjust spacing for proper star alignment
//   const typingSpeed = 250; // Speed of typing effect

//   useEffect(() => {
//     if (starLanded && typingIndex < brandName.length) {
//       const typingTimeout = setTimeout(() => {
//         setDisplayedText((prev) => prev + brandName[typingIndex]); // Append letters one by one
//         setStarPosition((typingIndex + 1) * letterSpacing); // Move star accordingly
//         setTypingIndex((prev) => prev + 1);
//       }, typingSpeed);

//       return () => clearTimeout(typingTimeout);
//     } else if (starLanded) {
//       setTimeout(() => {
//         setDisplayedText(""); // Clear text after pause
//         setTypingIndex(0); // Restart typing
//         setStarPosition(0); // Reset star to start
//       }, 2000); // Wait before restarting
//     }
//   }, [typingIndex, starLanded]);

//   useEffect(() => {
//     if (!isUserTyping) {
//       const interval = setInterval(() => {
//         setCurrentPrompt((prev) => (prev + 1) % prompts.length);
//       }, 3000); // Change prompt every 3 seconds

//       return () => clearInterval(interval);
//     }
//   }, [isUserTyping]);

//   return (
//     <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white px-4 relative overflow-hidden">
//       {/* Background Animation */}
//       <motion.div
//         className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/10 to-gray-800/20 blur-3xl opacity-50"
//         animate={{ scale: [1, 1.2, 1] }}
//         transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//       />

//       {/* Typing Brand Name with Star Animation */}
//       <motion.div className="relative flex items-center">
//         {/* Placeholder to prevent misalignment */}
//         <motion.h1
//           className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg min-w-[250px]"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 1 }}
//         >
//           {displayedText}
//         </motion.h1>

//         {/* Star Icon Animation (Drops from Top Before Typing) */}
//         <motion.div
//           className="absolute text-white"
//           initial={{ opacity: 0, y: -100 }} // Start above the screen
//           animate={{
//             opacity: 1,
//             y: 0, // Drop down to normal position
//             x: starPosition, // Moves dynamically per letter
//           }}
//           transition={{
//             type: "spring",
//             stiffness: 100,
//             damping: 15,
//             duration: 1,
//           }}
//           onAnimationComplete={() => setStarLanded(true)} // Start typing once the star lands
//         >
//           <BsStars className="text-4xl md:text-5xl animate-pulse" /> {/* Star Pulses Lightly */}
//         </motion.div>
//       </motion.div>

//       {/* Chat Box (User Input + Fading Prompts) */}
//       <motion.div
//         className="relative mt-10 w-full max-w-3xl h-24 bg-white/10 backdrop-blur-xl text-white rounded-3xl px-6 py-6 flex items-center justify-between shadow-xl text-2xl"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1, duration: 1 }}
//       >
//         {/* Input Field */}
//         <input
//           type="text"
//           className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-xl"
//           placeholder={prompts[currentPrompt]}
//           value={inputText}
//           onChange={(e) => {
//             setInputText(e.target.value);
//             setIsUserTyping(e.target.value.length > 0);
//           }}
//           onBlur={() => setIsUserTyping(false)}
//         />

//         {/* Animated Icon */}
//         <motion.button
//           className="bg-gray-700/50 p-5 rounded-full hover:bg-gray-600/50 transition-all"
//           whileHover={{ scale: 1.1 }}
//           whileTap={{ scale: 0.9 }}
//         >
//           <BsStars className="text-white text-3xl" />
//         </motion.button>
//       </motion.div>

//       {/* Quick Actions */}
//       <motion.div
//         className="mt-6 flex flex-wrap justify-center gap-4"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1.5, duration: 1 }}
//       >
//         {["Search with AI", "Ask Neuroline Ki", "Explore AI Utilities", "More"].map((label, index) => (
//           <button
//             key={index}
//             className="px-6 py-3 border border-gray-500/50 text-gray-300 rounded-full text-lg hover:bg-gray-700/50 transition-all backdrop-blur-md"
//           >
//             {label}
//           </button>
//         ))}
//       </motion.div>
//     </div>
//   );
// };

// export default HeroSection;


// import React, { useState, useEffect } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { BsStars } from "react-icons/bs"; // Creative AI-themed icon

// const prompts = [
//   "What is Neuroline Ki?",
//   "Find AI tools for productivity",
//   "Explain deep learning in simple terms",
//   "Generate insights from data",
//   "How can AI improve my workflow?",
// ];

// const brandName = "neUro Ki";

// const HeroSection = () => {
//   const [inputText, setInputText] = useState("");
//   const [currentPrompt, setCurrentPrompt] = useState(0);
//   const [isUserTyping, setIsUserTyping] = useState(false);
//   const [displayedText, setDisplayedText] = useState("");
//   const [typingIndex, setTypingIndex] = useState(0);

//   useEffect(() => {
//     const typingInterval = setInterval(() => {
//       if (typingIndex < brandName.length) {
//         setDisplayedText((prev) => prev + brandName[typingIndex]);
//         setTypingIndex((prev) => prev + 1);
//       } else {
//         clearInterval(typingInterval);
//       }
//     }, 200); // Adjust speed for the typing effect

//     return () => clearInterval(typingInterval);
//   }, [typingIndex]);

//   useEffect(() => {
//     if (!isUserTyping) {
//       const interval = setInterval(() => {
//         setCurrentPrompt((prev) => (prev + 1) % prompts.length);
//       }, 3000); // Change prompt every 3 seconds

//       return () => clearInterval(interval);
//     }
//   }, [isUserTyping]);

//   return (
//     <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white px-4 relative overflow-hidden">
//       {/* Background Animation */}
//       <motion.div
//         className="absolute inset-0 bg-gradient-to-r from-gray-800/20 via-gray-700/10 to-gray-800/20 blur-3xl opacity-50"
//         animate={{ scale: [1, 1.2, 1] }}
//         transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
//       />

//       {/* Branding */}
//       {/* <motion.h1
//         className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg"
//         initial={{ opacity: 0, y: -20 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ duration: 1 }}
//       >
//         neUro Ki
//       </motion.h1>
//       <motion.p
//         className="mt-4 text-lg text-gray-300 text-center"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 0.5, duration: 1 }}
//       >
//         AI-powered utility for enhanced productivity and intelligence
//       </motion.p> */}
//       <motion.div className="relative">
//         <motion.h1
//           className="text-4xl md:text-6xl font-extrabold text-center text-white drop-shadow-lg"
//           initial={{ opacity: 0 }}
//           animate={{ opacity: 1 }}
//           transition={{ duration: 1 }}
//         >
//           {displayedText}
//         </motion.h1>

//         {/* White Ball Animation */}
//         <motion.div
//           className="absolute w-3 h-3 md:w-4 md:h-4 bg-white rounded-full"
//           initial={{ opacity: 0, x: 0 }}
//           animate={{
//             opacity: 1,
//             x: typingIndex * 22, // Adjust spacing dynamically
//           }}
//           transition={{
//             type: "tween",
//             ease: "easeInOut",
//             duration: 0.2,
//           }}
//         />
//       </motion.div>

//       {/* Chat Box (User Input + Fading Prompts) */}
//       <motion.div
//         className="relative mt-10 w-full max-w-3xl h-34 bg-white/10 backdrop-blur-xl text-white rounded-3xl px-6 py-6 flex items-center justify-between shadow-xl text-2xl"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1, duration: 1 }}
//       >
//         {/* Input Field */}
//         <input
//           type="text"
//           className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-xl"
//           placeholder={prompts[currentPrompt]}
//           value={inputText}
//           onChange={(e) => {
//             setInputText(e.target.value);
//             setIsUserTyping(e.target.value.length > 0);
//           }}
//           onBlur={() => setIsUserTyping(false)}
//         />

//         {/* Animated Icon */}
//         <motion.button
//           className="bg-gray-700/50 p-5 rounded-full hover:bg-gray-600/50 transition-all"
//           whileHover={{ scale: 1.1 }}
//           whileTap={{ scale: 0.9 }}
//         >
//           <BsStars className="text-white text-3xl" />
//         </motion.button>
//       </motion.div>

//       {/* Quick Actions */}
//       <motion.div
//         className="mt-6 flex flex-wrap justify-center gap-4"
//         initial={{ opacity: 0, y: 10 }}
//         animate={{ opacity: 1, y: 0 }}
//         transition={{ delay: 1.5, duration: 1 }}
//       >
//         {["Search with AI", "Ask Neuroline Ki", "Explore AI Utilities", "More"].map((label, index) => (
//           <button
//             key={index}
//             className="px-6 py-3 border border-gray-500/50 text-gray-300 rounded-full text-lg hover:bg-gray-700/50 transition-all backdrop-blur-md"
//           >
//             {label}
//           </button>
//         ))}
//       </motion.div>
//     </div>
//   );
// };

// export default HeroSection;


// import React, { useState, useEffect } from "react";
// import Particles from "react-tsparticles";
// import { motion } from "framer-motion";

// const Hero: React.FC = () => {
//   const [typedText, setTypedText] = useState<string>("");
//   const [currentLineIndex, setCurrentLineIndex] = useState<number>(0);
//   const [cursorVisible, setCursorVisible] = useState<boolean>(true);

//   const fullText = ["Neuroline", "KI", "your agents", "the expert"];
//   const typingSpeed = 150;
//   const linePause = 1000;

//   useEffect(() => {
//     if (currentLineIndex >= fullText.length) return;

//     const currentLine = fullText[currentLineIndex];
//     const nextCharIndex = typedText.length;

//     if (nextCharIndex < currentLine.length) {
//       const typingTimeout = setTimeout(() => {
//         setTypedText((prevText) => prevText + currentLine[nextCharIndex]);
//       }, typingSpeed);

//       return () => clearTimeout(typingTimeout);
//     } else {
//       const lineTimeout = setTimeout(() => {
//         setTypedText("");
//         setCurrentLineIndex((prevIndex) => prevIndex + 1);
//       }, linePause);

//       return () => clearTimeout(lineTimeout);
//     }
//   }, [typedText, currentLineIndex]);

//   useEffect(() => {
//     const cursorInterval = setInterval(() => {
//       setCursorVisible((prev) => !prev);
//     }, 500);

//     return () => clearInterval(cursorInterval);
//   }, []);

//   const spheres = [
//     { text: "Explore AI", video: "/assets/robot.mp4" },
//     { text: "Discover Future", video: "/assets/ai1.mp4" },
//     { text: "Innovate Today", video: "/assets/ai2.mp4" },
//   ];

//   return (
//     <div className="relative h-screen w-screen overflow-hidden text-white">
//       <div className="absolute inset-0 ">
//         <video autoPlay loop muted className="w-full h-full object-cover">
//           <source src="/assets/ai.mp4" type="video/mp4" />
//           Your browser does not support the video tag.
//         </video>
//       </div>

//       {/* Particle Overlay */}
//       <div className="absolute inset-0 -z-10">
//         <Particles
//           options={{
//             background: { color: { value: "transparent" } },
//             fpsLimit: 60,
//             particles: {
//               number: { value: 50 },
//               color: { value: "#ffffff" },
//               shape: { type: "circle" },
//               opacity: { value: 0.5 },
//               size: { value: 4 },
//               move: { enable: true, speed: 2 },
//             },
//           }}
//         />
//       </div>

//       {/* Typing Effect */}
//       <div className="absolute inset-0 flex flex-col justify-center items-start px-6 sm:px-16 space-y-4 sm:space-y-6 z-10">
//         {fullText.slice(0, currentLineIndex).map((line, index) => (
//           <h1
//             key={index}
//             className="text-[2rem] sm:text-[4rem] md:text-[6rem] lg:text-[8rem] font-extrabold uppercase leading-none"
//           >
//             {line}
//           </h1>
//         ))}

//         <h1 className="text-[2rem] sm:text-[4rem] md:text-[6rem] lg:text-[8rem] font-extrabold uppercase leading-none">
//           {typedText}
//           <span
//             className={`blinking-cursor ${
//               cursorVisible ? "visible" : "invisible"
//             }`}
//           >
//             |
//           </span>
//         </h1>
//       </div>

//       {/* Interactive Movable Spheres */}
//       {spheres.map((sphere, index) => (
//         <motion.div
//           key={index}
//           className="absolute w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] md:w-[400px] md:h-[400px] rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-purple-500 shadow-2xl cursor-pointer z-20"
//           drag
//           dragConstraints={{
//             top: -100,
//             left: -100,
//             right: window.innerWidth - 200,
//             bottom: window.innerHeight - 200,
//           }}
//           initial={{
//             x: 100 * index,
//             y: -200 + index * 50,
//           }}
//           animate={{
//             y: 150 + index * 100,
//           }}
//           transition={{
//             type: "spring",
//             stiffness: 50,
//             damping: 10,
//             delay: index * 0.2,
//           }}
//           whileHover={{
//             scale: 1.1,
//             rotate: [0, 5, -5, 0],
//           }}
//         >
//           {/* Sphere Video */}
//           <video
//             autoPlay
//             loop
//             muted
//             className="absolute w-full h-full object-cover"
//             style={{
//               clipPath: "circle(50% at 50% 50%)",
//             }}
//           >
//             <source src={sphere.video} type="video/mp4" />
//             Your browser does not support the video tag.
//           </video>

//           {/* Text Overlay */}
//           <div className="absolute inset-0 flex items-center justify-center">
//             <h2 className="text-xs sm:text-sm md:text-lg font-bold text-center text-white px-2">
//               {sphere.text}
//             </h2>
//           </div>
//         </motion.div>
//       ))}

//       {/* CSS for Blinking Cursor */}
//       <style>{`
//         .blinking-cursor {
//           display: inline-block;
//           width: 12px;
//           height: 2.5rem;
//           background-color: white;
//           margin-left: 5px;
//         }
//         @keyframes blink {
//           50% {
//             opacity: 0;
//           }
//         }
//         .invisible {
//           opacity: 0;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default Hero;


// // import React, { useState, useEffect } from "react";
// // import Particles from "react-tsparticles";
// // import { motion } from "framer-motion";

// // const Hero: React.FC = () => {
// //   const [typedText, setTypedText] = useState<string>(""); // Current text being typed
// //   const [currentLineIndex, setCurrentLineIndex] = useState<number>(0); // Track the current line being typed
// //   const [cursorVisible, setCursorVisible] = useState<boolean>(true); // Blinking cursor visibility

// //   const fullText = ["Neuroline", "KI", "Talk with", "The Expert"]; // Lines of text to type
// //   const typingSpeed = 150; // Speed in milliseconds for typing each letter
// //   const linePause = 100; // Pause between lines in milliseconds

// //   useEffect(() => {
// //     if (currentLineIndex >= fullText.length) return;

// //     const currentLine = fullText[currentLineIndex];
// //     const nextCharIndex = typedText.length;

// //     if (nextCharIndex < currentLine.length) {
// //       const typingTimeout = setTimeout(() => {
// //         setTypedText((prevText) => prevText + currentLine[nextCharIndex]);
// //       }, typingSpeed);

// //       return () => clearTimeout(typingTimeout);
// //     } else {
// //       const lineTimeout = setTimeout(() => {
// //         setTypedText("");
// //         setCurrentLineIndex((prevIndex) => prevIndex + 1);
// //       }, linePause);

// //       return () => clearTimeout(lineTimeout);
// //     }
// //   }, [typedText, currentLineIndex]);

// //   useEffect(() => {
// //     const cursorInterval = setInterval(() => {
// //       setCursorVisible((prev) => !prev);
// //     }, 500);

// //     return () => clearInterval(cursorInterval);
// //   }, []);

// //   const spheres = [
// //     { text: "Explore AI", video: "/assets/robot.mp4" },
// //     { text: "Discover Future", video: "/assets/ai1.mp4" },
// //     { text: "Innovate Today", video: "/assets/ai2.mp4" },
// //   ];

// //   return (
// //     <div className="relative h-screen w-screen overflow-hidden text-white">
// //       {/* Background Video */}
//       // <div className="absolute inset-0 -z-20">
//       //   <video
//       //     autoPlay
//       //     loop
//       //     muted
//       //     className="w-full h-full object-cover"
//       //   >
//       //     <source src="/assets/ai.mp4" type="video/mp4" />
//       //     Your browser does not support the video tag.
//       //   </video>
//       // </div>

// //       {/* Particle Overlay */}
// //       <div className="absolute inset-0 -z-10">
// //         <Particles
// //           options={{
// //             background: { color: { value: "transparent" } },
// //             fpsLimit: 60,
// //             particles: {
// //               number: { value: 50 },
// //               color: { value: "#ffffff" },
// //               shape: { type: "circle" },
// //               opacity: { value: 0.5 },
// //               size: { value: 4 },
// //               move: { enable: true, speed: 2 },
// //             },
// //           }}
// //         />
// //       </div>

// //       {/* Typing Effect */}
// //       <div className="absolute inset-0 flex flex-col justify-center items-start px-16 space-y-6 z-10">
// //         {fullText.slice(0, currentLineIndex).map((line, index) => (
// //           <h1
// //             key={index}
// //             className="text-[6rem] md:text-[8rem] font-extrabold uppercase leading-none"
// //           >
// //             {line}
// //           </h1>
// //         ))}

// //         <h1 className="text-[3rem] md:text-[5rem] font-extrabold uppercase leading-none">
// //           {typedText}
// //           <span
// //             className={`blinking-cursor ${
// //               cursorVisible ? "visible" : "invisible"
// //             }`}
// //           >
// //             |
// //           </span>
// //         </h1>
// //       </div>

// //       {/* Interactive Movable Spheres */}
// //       {spheres.map((sphere, index) => (
// //         <motion.div
// //           key={index}
// //           className="absolute w-[400px] h-[400px] rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-purple-500 shadow-2xl cursor-pointer z-20"
// //           drag
// //           dragConstraints={{
// //             top: -100,
// //             left: 800 + index * 100, 
// //             right: window.innerWidth - 300, 
// //             bottom: 600,
// //           }}
// //           initial={{ x: 1200 + index * 100, y: -500 }} 
// //           animate={{ y: 150 + index * 200 }} 
// //           transition={{
// //             type: "spring",
// //             stiffness: 50,
// //             damping: 10,
// //             delay: index * 0.2, 
// //           }}
// //           whileHover={{
// //             scale: 1.1,
// //             rotate: [0, 5, -5, 0], 
// //           }}
// //           style={{
// //             perspective: 1000,
// //           }}
// //         >
// //           {/* Sphere Video */}
// //           <video
// //             autoPlay
// //             loop
// //             muted
// //             className="absolute w-full h-full object-cover"
// //             style={{
// //               clipPath: "circle(50% at 50% 50%)", // Make it circular
// //             }}
// //           >
// //             <source src={sphere.video} type="video/mp4" />
// //             Your browser does not support the video tag.
// //           </video>

// //           {/* Text Overlay */}
// //           <div className="absolute inset-0 flex items-center justify-center">
// //             <h2 className="text-xl md:text-3xl font-bold text-center text-white px-4">
// //               {sphere.text}
// //             </h2>
// //           </div>
// //         </motion.div>
// //       ))}

// //       {/* CSS for Blinking Cursor */}
// //       <style>{`
// //         .blinking-cursor {
// //           display: inline-block;
// //           width: 12px;
// //           height: 2.5rem;
// //           background-color: white;
// //           margin-left: 5px;
// //         }
// //         @keyframes blink {
// //           50% {
// //             opacity: 0;
// //           }
// //         }
// //         .invisible {
// //           opacity: 0;
// //         }
// //       `}</style>
// //     </div>
// //   );
// // };

// // export default Hero;
